import { action_fist, action_gun } from '@assets/icons';
import { CharacterNumberInput, MenuTop, Output, RadioItem, Text } from '@components';
import { ActionGenreChildOptionValueEnum, CharacterNumberInputType, styleConstant } from '@constant';
import { useOutput } from '@hooks';
import { RadioGroup } from '@mui/material';
import { useActionGenreActions, useGeneralActions } from '@remote-hooks';
import { colorSet } from '@theme';
import { useEffect, useState } from 'react';

const ActionGenreOutputPage = () => {
  const { characterNumber, setCharacterNumber, onSetGeneral, storedActionGenre, onSetActionGenre } = useOutput();

  // 인원
  const [topFilterHeight, setTopFilterHeight] = useState(
    styleConstant.topFilterHeightBeforeScrollDownActionGenreOutput
  );
  // 스크롤다운에 따른 layout 분기용 state
  const [isScrollDown, setIsScrollDown] = useState<boolean>(false);
  const [isScrollEventListenerEnabled, setIsScrollEventListenerEnabled] = useState(false);
  // 마운트 상태를 추적하는 state
  const [isComponentMounted, setIsComponentMounted] = useState(false);

  const { onInitGeneral } = useGeneralActions();
  const { onInitActionGenre } = useActionGenreActions();

  // Scroll event handler
  const handleScroll = () => {
    const scrollY = window.scrollY;

    // Calculate new height for the topFilter based on scroll position
    let newHeight = styleConstant.topFilterHeightBeforeScrollDownActionGenreOutput - scrollY;

    //topFilter의 레이아웃 변화가 필요할 만큼 충분히 스크롤다운
    if (newHeight < styleConstant.topFilterHeightAfterScrollDown) {
      newHeight = styleConstant.topFilterHeightAfterScrollDown;
    }

    //최상단으로 다시 스크롤업
    if (newHeight > styleConstant.topFilterHeightBeforeScrollDownActionGenreOutput) {
      newHeight = styleConstant.topFilterHeightBeforeScrollDownActionGenreOutput;
    }

    setTopFilterHeight(newHeight);

    if (newHeight < styleConstant.topFilterHeightAfterScrollDown * 1.8) {
      setIsScrollDown(true);
    } else {
      setIsScrollDown(false);
    }
  };

  useEffect(() => {
    const browserHeight = window.innerHeight;
    const pageElement = document.getElementById('action-genre-output-id');
    const minRoomForHandleScroll =
      styleConstant.topFilterHeightBeforeScrollDownActionGenreOutput - styleConstant.topFilterHeightAfterScrollDown;

    if (pageElement) {
      // ResizeObserver를 사용하여 높이 변화 감지
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const pageHeight = entry.target.scrollHeight;
          setIsScrollEventListenerEnabled(pageHeight - browserHeight >= minRoomForHandleScroll);
        }
      });

      resizeObserver.observe(pageElement);

      // cleanup
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  useEffect(() => {
    if (isScrollEventListenerEnabled) {
      // Attach the scroll event listener
      window.addEventListener('scroll', handleScroll);

      return () => {
        // Cleanup on component unmount
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isScrollEventListenerEnabled]);

  useEffect(() => {
    setIsComponentMounted(true);
    if (!isComponentMounted) return;

    return () => {
      onInitGeneral();
      onInitActionGenre();
    };
  }, [isComponentMounted, onInitGeneral, onInitActionGenre]);

  return (
    <div id="action-genre-output-id" style={{ display: 'flex', flexDirection: 'column' }}>
      <MenuTop pageName={isScrollDown ? '액션 장르' : undefined} />

      {/* 옵션 */}
      <div
        className="topFilter"
        style={{
          height: topFilterHeight, // 동적으로 설정된 높이 적용
          backgroundColor: colorSet.white,
          position: 'sticky', // 고정
          top: styleConstant.menuTopHeight, // MenuTop 바로 아래에 고정
          zIndex: styleConstant.zIndexTopFilter, // 다른 요소보다 위에 표시되도록 설정
          padding: isScrollDown ? '0px 40px' : '0px 40px',
          borderBottom: `1px solid ${colorSet.gray300}`,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <div>
          {isScrollDown ? null : (
            <Text variant="h2" fontWeight="bold" marginBottom="24px">
              액션 장르
            </Text>
          )}

          <div>
            {/* 인원 */}
            <div
              style={{
                display: 'flex',
                flexDirection: isScrollDown ? 'row' : 'column',
                alignItems: isScrollDown ? 'center' : 'flex-start'
              }}
            >
              <Text
                variant="subtitle"
                fontWeight="bold"
                style={{
                  margin: isScrollDown ? '0px 20px 0px 0px' : '0px 0px 8px 0px'
                }}
              >
                인원
              </Text>
              <CharacterNumberInput
                type={CharacterNumberInputType.SELECT}
                characterNumber={{
                  maleNumber: 0,
                  femaleNumber: 0,
                  wholeGenderNumber: characterNumber.wholeGenderNumber,
                  isWholeGenderNumberChecked: true
                }}
                onWholeGenderNumberChange={(value: number | string) => {
                  setCharacterNumber({ ...characterNumber, wholeGenderNumber: value });
                }}
                onApplyClick={(values: {
                  maleNumber?: number | string;
                  femaleNumber?: number | string;
                  wholeGenderNumber?: number | string;
                  isWholeGenderNumberChecked?: boolean;
                }) => {
                  onSetGeneral({
                    wholeGenderNumber: values.wholeGenderNumber
                  });
                }}
                isWholeGenderNumberOnly={true}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', marginTop: topFilterHeight }}>
        {/* 좌측 메뉴 */}
        <div
          style={{
            width: styleConstant.outputPageLeftMenuWidth,
            height: `calc(100vh - ${styleConstant.menuTopHeight + styleConstant.topFilterHeightAfterScrollDown}px)`,
            padding: '24px 32px',
            backgroundColor: colorSet.gray100,
            borderRight: `1px solid ${colorSet.gray300}`,
            position: 'fixed',
            top: `${styleConstant.menuTopHeight + topFilterHeight}px`
          }}
        >
          <Text variant="body1" fontWeight="bold">
            하위 스페셜 단추
          </Text>
          <div style={{ width: 224, padding: '8px 12px 8px 0px', marginTop: '8px' }}>
            <RadioGroup
              value={storedActionGenre.childOptionValue}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onSetActionGenre({
                  childOptionValue: event.target.value as ActionGenreChildOptionValueEnum
                });
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '24px 12px'
                }}
              >
                <RadioItem
                  label="펀치"
                  value={ActionGenreChildOptionValueEnum.펀치}
                  image={action_fist}
                  imageSize={48}
                  onChange={(value) => {
                    onSetActionGenre({
                      childOptionValue: value as ActionGenreChildOptionValueEnum
                    });
                  }}
                />
                <RadioItem
                  label="화기(총포류)"
                  value={ActionGenreChildOptionValueEnum.화기}
                  image={action_gun}
                  imageSize={48}
                  onChange={(value) => {
                    onSetActionGenre({
                      childOptionValue: value as ActionGenreChildOptionValueEnum
                    });
                  }}
                />
              </div>
            </RadioGroup>
          </div>
        </div>

        {/* 검색 결과 */}
        <div
          style={{
            width: '100%',
            marginLeft: styleConstant.outputPageLeftMenuWidth,
            marginTop: -topFilterHeight
          }}
        >
          <Output />
        </div>
      </div>
    </div>
  );
};

export default ActionGenreOutputPage;
